import { useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from "@mui/material";
import {
  Shield as ShieldIcon,
  VpnKey as VpnKeyIcon,
  Lock as LockIcon,
  Warning as WarningIcon,
  Google as GoogleIcon,
  FileUpload as FileUploadIcon,
} from "@mui/icons-material";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@context/NotificationContext";
import { stashPendingKeyImport } from "@hooks/useLocalKeys";
import { parseKeyBundleJson } from "@/utils/keyBundle";
import { PageLoading } from "@components/PageLoading";
import { routes } from "@/constants/routes";

export default function Welcome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, loading, loginWithGoogle } = useAuth();
  const { notify } = useNotification();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(routes.dashboard, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (loading) {
    return <PageLoading />;
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "80vh",
        justifyContent: "center",
      }}
    >
      <ShieldIcon sx={{ fontSize: 72, color: "primary.main", mb: 2 }} />
      <Typography
        variant="h3"
        gutterBottom
        sx={{
          fontWeight: 700,
          textAlign: "center",
          fontSize: { xs: "1.75rem", sm: "2.125rem", md: "3rem" },
        }}
      >
        {t("app.name")}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          color: "text.secondary",
          textAlign: "center",
          mb: 4,
          fontSize: { xs: "1rem", sm: "1.25rem" },
          px: { xs: 0, sm: 2 },
        }}
      >
        {t("welcome.title")}
      </Typography>
      <Card
        sx={{
          maxWidth: 480,
          width: "100%",
          mb: 4,
          bgcolor: "background.paper",
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("welcome.securityFeatures")}
          </Typography>

          <List dense>
            <ListItem>
              <ListItemIcon>
                <VpnKeyIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary={t("welcome.localKeys")} />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <LockIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary={t("welcome.encrypted")} />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <WarningIcon color="warning" />
              </ListItemIcon>
              <ListItemText primary={t("welcome.keyWarning")} />
            </ListItem>
          </List>
        </CardContent>
      </Card>
      <Button
        variant="contained"
        size="large"
        startIcon={<GoogleIcon />}
        onClick={loginWithGoogle}
        sx={{
          mb: 1,
          px: 4,
          py: 1.5,
          textTransform: "none",
          fontSize: "1.1rem",
        }}
      >
        {t("welcome.signIn")}
      </Button>
      <Button
        component={RouterLink}
        to="/preview"
        variant="outlined"
        size="large"
        sx={{
          mb: 2,
          px: 4,
          py: 1.5,
          textTransform: "none",
          fontSize: "1.05rem",
        }}
      >
        {t("preview.cta")}
      </Button>
      <Link
        component="button"
        variant="body2"
        sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}
        onClick={() => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = ".json,application/json";
          input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
              const text = reader.result as string;
              const bundle = parseKeyBundleJson(text);
              if (!bundle) {
                notify(t("importKeys.invalidFile"), "error");
                return;
              }
              stashPendingKeyImport(text);
              notify(t("importKeys.stashed"), "info");
            };
            reader.readAsText(file);
          };
          input.click();
        }}
      >
        <FileUploadIcon fontSize="small" />
        {t("welcome.importKeys")}
      </Link>
    </Box>
  );
}
